import type { Item } from './types';

/**
 * The furniture classes that decide what may collide with what.
 *
 * Unity does not ask geometry whether two pieces may share space — it asks the catalog. A vase,
 * a tray, a cooktop, a countertop is *meant* to stand on something, so it never takes part in the
 * check (`TopFurnitureHandler.ChangeHighlightingTablePlanes`); only pieces that provide a surface
 * are compared with each other, and flat decor only with flat decor. These sets are the four tag
 * groups that rule reads, copied verbatim from the Unity attributes on `/Config` in
 * `HomeDesign.Unity/Assets/Resources/Config.xml` (`TablePlaneFurniture`, `TableTopFurniture`,
 * `PlaneDecor`, `AbstractPicture`), in their source order, duplicates dropped. Re-copy them when
 * the native config changes; do not curate them here.
 */

/** Pieces that carry other things: tables, seating, beds, cabinets, carpets. */
const TABLE_PLANE: ReadonlySet<string> = new Set([
  'LaptopStand', 'WallDesk', 'JuniorTable', 'WorkStation', 'ModernDesk', 'ClassicDesk', 'StorageDesk',
  'CornerDesk', 'GamingDesk', 'Desk', 'Table', 'LadderDesk', 'Roofs', 'Painting', 'TablePlaneFurniture',
  'Sticker', 'Aquarium', 'Appliances', 'Refrigerators', 'Basket', 'TrashCan', 'Pillow', 'Primitives',
  'Drawer', 'KidsDrawer', 'Washer', 'WashersDryers', 'VerticalWasher', 'Dryer', 'InteriorWall',
  'WallCabinet', 'WallShelve', 'Bookshelve', 'HighCabinet', 'SingleHighCabinet', 'DoubleHighCabinet',
  'OpenHighCabinet', 'Piano', 'Pantry', 'Pouf', 'KitchenShelf', 'DiningSet', 'KitchenSink',
  'KitchenWall', 'ShoeCabinet', 'Bench', 'KitchenSurface', 'KitchenIsland', 'KitchenCart', 'KidChair',
  'KidTable', 'Ovens', 'KitchenElectronics', 'KitchenTableElectronics', 'Cooktop', 'Crib',
  'StandardCrib', 'ConvertibleCrib', 'RattanCrib', 'MeshCrib', 'Cradle', 'StorageCrib', 'OvalCrib',
  'CribDecor', 'Bed', 'JuniorBed', 'Chair', 'Armchair', 'BarStool', 'Stool', 'Ottoman',
  'ArmchairWithOttoman', 'HangingChair', 'WingbackChair', 'BarrelChair', 'SwingChair', 'SwanChair',
  'ClubChair', 'ReclinerChair', 'ChesterfieldChair', 'AccentChair', 'SlipperChair', 'CornerChair',
  'RoundChair', 'EggChair', 'AdirondackChair', 'Stairs', 'Chaise', 'Footstool', 'Dishwasher',
  'BuiltinDishwasher', 'BaseCabinet', 'DiningTable', 'Buffet', 'TVset', 'TVStorage', 'Fireplace',
  'Sideboard', 'ConsoleTable', 'Dresser', 'TVStand', 'CoffeeTable', 'SideTable', 'Wardrobe',
  'DressingTable', 'Nightstand', 'Sofa', 'CornerSofa', 'Range', 'AnimalFurniture', 'Carpet', 'Niche',
  'RockingChair', 'Bookcase', 'FloorCab', 'WallCabinetRegular', 'KidsCabinet', 'RoundTable',
  'OvalTable', 'SquareTable', 'RectangleTable', 'Cubicle', 'CommercialSeating', 'Pool', '2DPool',
  'Sunbed', 'Tray', 'Loveseat', 'RightSofa', 'Lounge', 'ChesterfieldSofa', 'CurvedSofa', 'RoundSofa',
  'PshapedSofa', 'TuxedoSofa', 'MarshmallowSofa', 'SetteeSofa', 'SleeperSofa', 'SofaConstructor',
  '2seat', '3seat', 'CafeSeating', 'Countertop', 'GamingSetup', 'GameTable', 'GamingChair',
  'GamingTable', 'Printer', 'Stroller', 'DryingCabinet', 'StandPlant', 'Runner', 'KidsRug',
  'AbstractRug', 'RoundRug', 'DoorMat', 'BathMat', 'PrayerMat', 'SkylightRoof', 'Conditioner',
  'Radiator', 'FloorRadiator', 'Billiard', 'Gym', 'Sport', 'Speaker', 'Recorder', 'Playground',
  'OfficeChair', 'JuniorChair', 'ExecutiveChair', 'Tribune', 'ReceptionChair', 'TheaterChair',
  'OutdoorSofa', 'OutdoorChair', 'OutdoorSetSoft', 'OutdoorBench', 'Swings', 'HighChair',
  'NonUpholsteredChair', 'UpholsteredChair', 'FoldingChair', 'ConferenceChair', 'SchoolChair',
  'TulipChair', 'ButterflyChair', 'SwivelChair', 'CareChair', 'StepStool', 'BarTable', 'SwivelBarstool',
  'LowBackBarstool', 'FullBackBarstool', 'BacklessBarstool', 'RoundBarstool', 'ComFurniture',
  'CoworkingSeats', 'OutdoorTable', 'OutdoorSeating', 'OutdoorSet', 'FourPoster', 'DoubleBed',
  'FullSize', 'Queen', 'Twin', 'King', '2LevelsBed', 'FoldingBed', 'CornerBed', 'RoundBed', 'Blanket',
  'Mattress', 'StorageRack', 'CornerShelve', 'OfficeShelving', 'LadderShelf', 'LowerShelving',
  'WardrobeSystem', 'ClothesRack', 'SlidingWardrobe', 'WardrobeCabinet', 'Locker', 'CabinetTall',
  'CornerCabinetLow', 'CornerCabinetHigh', 'CoatStand', 'KidsShelve', 'Hutch', 'OfficeStorage',
  'Secretary', 'StaircaseCabinet', 'StorageUnit', 'ArtShelve', 'PapersShelve', 'SquareShelve',
  'OpenShelve', 'ClosedShelve', 'BathroomStorage', 'KitchenStorage', 'WineCabinet', 'KitchenSet',
  'Bridge', 'Stones', 'GardenDecor', 'Roads', 'Drain', 'Drain3D', 'Luke', 'Fountain', 'Balcony',
  'Porch', 'Gazebo', 'Hammock', 'Grill', 'Changer', 'TowelStand', 'CafeFurniture', 'Car',
  'SportTransport', 'Ironing', 'GasCooktop', 'CooktopBuiltin', 'ElectricCooktop', '1DoorFridge',
  '2DoorFridge', 'MiniFridge', 'OpenFridge', 'WineFridge', 'Microwave', 'CombinedOven', 'GasOven',
  'ElectricOven', 'TwinBedding', 'QueenBedding', 'KingBedding', 'StorageBench', 'WoodenBench',
  'UpholsteredBench', 'OvalBench', 'ArmrestBench', 'BackBench', 'ShelveBench', 'DoorNightstand',
  'DrawerNightstand', '1DoorNightstand', 'ShelveNightstand', 'RoundNightstand', 'CornerNightstand',
  'NoLegsNightstand', 'SolidNightstand', 'FloorSink', 'SinkCabinet',
]);

/** Pieces that are carried: vases, jars, cooktops, counters, pillows, lamps. */
const TABLE_TOP: ReadonlySet<string> = new Set([
  'WallTap', 'WallToilet', 'RoomDecor', 'CoffeeMaker', 'Toaster', 'Microwave', 'Kettle',
  'TableTopFurniture', 'Objects2D', 'BuiltinWardrobe', 'People', 'CharacterOther', 'Toilet',
  'Primitives', 'Painting', 'Appliances', 'Aquarium', 'FishTank', 'Sticker', 'WallPlant', 'Rug',
  'WiringDevices', 'WallSconces', 'CeilingLamp', 'WashersDryers', 'Washer', 'VerticalWasher', 'Dryer',
  'Vase', 'Plant', 'Pillow', 'Candle', 'Books', 'TableLamp', 'Basket', 'TrashCan', 'Electronics', 'Cat',
  'Dog', 'Clock', 'KitchenSurface', 'KitchenFood', 'Cooktop', 'Speaker', 'Recorder', 'TV',
  'KitchenTableElectronics', 'Signboards', 'Tray', 'OutdoorPlant', 'Bush', 'Flower', 'Tree', 'SinkBowl',
  'Faucet', 'PlaneElectronics', 'SurfaceDecor', 'Toys', 'Cleaning', 'KidsDecor', 'Plates', 'CupsMugs',
  'CuttingBoard', 'KitchenJars', 'DiningServing', 'Kitchenware', 'Drinks', 'Desserts', 'Statue',
  'Countertop', 'VR', 'Microphone', 'Headphones', 'Monitor', 'PC', 'Laptop', 'GameConsole', 'TVbox',
  'GamingDecor', 'Tablet', 'MobilePhone', 'Keyboard', 'Mouse', 'Printer', 'OfficeDecor', 'CeilingLED',
  'ChandelierLamp', 'PendantLamp', 'TrackLight', 'SpotLights', 'KitchenIslandLights',
  'SemiFlushMountLights', 'FlushMountLights', 'TelescopicLamp', 'OutdoorLight', 'OutdoorLighting',
  'TechLight', 'LEDlight', 'Blanket', 'Mattress', 'PoolMore', 'Umbrella', 'Blender', 'MultiCooker',
  'WaterPurifier', 'TableMirror', 'Cosmetics', 'Towel', 'Frames', 'PaintingFloor', 'Sign', 'Christmas',
  'Halloween', 'Wedding', 'AnimalFurniture', 'Pet', 'SkylightRoof', 'Roofs', 'Fan', 'Conditioner',
  'Thermometer', 'Purifier', 'SmartSpeaker', 'Movies', 'Gaming', 'Photo', 'GardenTools', 'Sewing',
  'Hobby', 'Lantern', 'DeskLamp', 'BedsideLamp', 'ArchedLamp', 'AccentLamp', 'BuffetLamp', 'KidLamp',
  'Clothes', 'Ingredients', 'Pans', 'GardenVase', 'TableVase', 'FloorVase', 'Stones', 'GardenDecor',
  'Sculpture', 'Soap', 'Shampoo', 'SoapHolder', 'CosmeticsFlat', 'ToothbrushHolder',
  'ToiletPaperHolder', 'ToiletBrush', 'ToiletSeat', 'BathroomDecor', 'HairDryer', 'VacuumCleaner',
  'Iron', 'Projector', 'Router', 'Antenna', 'HomeAppliances', 'PosSystem', 'ComEquipment',
  'ShowerScreen', 'ShowerMixer', 'SquarePillow', 'RoundPillow', 'RectanglePillow', 'AbstractPillow',
  'PillowSet', 'RollPillow', 'OrthopedicPillow', 'KnotPillow', 'SeatPillow', 'TwinBedding',
  'QueenBedding', 'KingBedding', 'Molding', 'Moldings', 'FloorSink',
]);

/** Flat things laid onto a surface, alongside the plain `Carpet` tag. */
const PLANE_DECOR: ReadonlySet<string> = new Set([
  '2DPool', 'Drain', 'Roads', 'Luke', 'PlaneElectronics', 'SurfaceDecor', 'CosmeticsFlat', 'Rug',
]);

/** Flat things hung on one. */
const ABSTRACT_PICTURE: ReadonlySet<string> = new Set([
  'Painting', 'Poster', 'Sticker', 'Flag', 'Appliances', 'ElevatorPanel', 'Objects2D',
  'AbstractPicture', 'Annotation',
]);

const tagged = (item: Item, group: ReadonlySet<string>) => (item.tags ?? []).some(tag => group.has(tag));

/** A piece that provides a surface — a table, a sofa, a cabinet — rather than standing on one.
 * A tag in both groups (a countertop, a primitive cube) means the catalog cannot tell, so it is
 * left out of the comparison. */
export function isSurfaceFurniture(item: Item): boolean {
  return tagged(item, TABLE_PLANE) && !tagged(item, TABLE_TOP);
}

/** A rug, a floor decal, a picture: flat things that share their plane with solid furniture by
 * design and can only be in each other's way. */
export function isFlatDecor(item: Item): boolean {
  return (item.tags ?? []).some(tag => tag === 'Carpet' || PLANE_DECOR.has(tag)) || tagged(item, ABSTRACT_PICTURE);
}

/** Whether being placed by hand can put this piece *in the way of* anything at all. An untagged
 * item answers no: without the catalog we cannot tell a sofa from a saucer, and a warning we are
 * not sure of is worse than none. */
export function obstructsWhenPlaced(item: Item): boolean {
  return tagged(item, TABLE_PLANE) && (isSurfaceFurniture(item) || isFlatDecor(item));
}

/** Whether the catalog gave this item `tag`. */
export const hasTag = (item: Item, tag: string): boolean => (item.tags ?? []).includes(tag);

/** Unity `IsTableTopFurniture()`: a piece meant to be carried by another — the only kind that
 * climbs onto one when dragged across it. */
export const isTableTopFurniture = (item: Item): boolean => tagged(item, TABLE_TOP);

/** Unity `IsTablePlaneFurniture()`: a piece with a surface to stand on. */
export const isTablePlaneFurniture = (item: Item): boolean => tagged(item, TABLE_PLANE);

/** Unity `IsPrimitive()`: an editor primitive — a cube, a slab — rather than a catalog model. */
export const isPrimitive = (item: Item): boolean => hasTag(item, 'Primitives');

/** Unity `IsCarpetLike()`: a rug or a floor decal, which lies on floors, slabs and other rugs only. */
export const isCarpetLike = (item: Item): boolean => (item.tags ?? []).some(tag => tag === 'Carpet' || PLANE_DECOR.has(tag));

/** Unity `IsPlant()`: plants are exempt from the size rule — a wide crown over a narrow stand. */
export const isPlant = (item: Item): boolean => hasTag(item, 'Plant');

/** Unity `IsStairs()`, tag for tag. */
const STAIRS: ReadonlySet<string> = new Set(['Stairs', 'StairsOpening', 'StairsOpeningRound', 'Ladder', 'RoundStairs', 'CornerStairs', 'StairsStraight90']);
export const isStairs = (item: Item): boolean => tagged(item, STAIRS);
